import { useState } from "react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import {
  Check,
  X,
  Plus,
  Pencil,
  Trash2,
  RemoveFormatting,
  View,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AlertDialog } from "@/widgets/AlertDailog";
import { PaginationControl } from "@/widgets/renderPagination";
import { useLocation } from "wouter";
import { Popup } from "@/widgets/RuleDetailDailog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { CreateFormDialog } from "../VersionRule/CreateFormDailog";
import { deleteWorkflowData, toggleActiveWorkflowStatus, toggleWorkflowStatus, useWorkflowRepository } from "@/hooks/useLocalStorage";
import { WorkflowAction } from "@/lib/api-services";
import { DeleteConfirmationDialog } from "@/widgets/DeleteModal";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";
import { Input } from "../ui/input";
import { ConfirmationDialog } from "@/widgets/AlertCommon";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useBreadcrumbs } from "../BreadcrumbContext";
import { CreateWorkflow } from "./CreateWorkflow";
import { CheckInOutDialog } from "./CheckInOutDialog";


interface TableHierarchicalNodeProps {
  topLevelData: WorkflowAction[];
  pageIndex: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onEditForm: (data: any) => void;
}

function TableForm({
  topLevelData,
  pageIndex,
  pageSize,
  totalCount,
  onPageChange,
  onEditForm,
}: TableHierarchicalNodeProps) {
  const [currentData, setCurrentData] = useState<WorkflowAction[]>([]);
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [reqStatus, setReqStatus] = React.useState(false);

  const [showPopup, setShowPopup] = React.useState(false);
  const [popupName, setPopupName] = React.useState("");
  const [headingData, setHeadingData] = React.useState<string | null>("");
  const [descriptionContent, setDescriptionContent] = React.useState<string | null>("");

  const [_location, setLocation] = useLocation();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<WorkflowAction | null>(null);

  const [toggleMode, setToggleMode] = useState<"checkinout" | "activate">("checkinout");

  const [checkInOutDialogOpen, setCheckInOutDialogOpen] = useState(false);
  const [itemToCheckInOut, setItemToCheckInOut] = useState<WorkflowAction | null>(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [itemToToggle, setItemToToggle] = useState<WorkflowAction | null>(null);

  const deletePortalForm = deleteWorkflowData();
  const togglePublishMutation = toggleWorkflowStatus();
  const statusActiveMutation = toggleActiveWorkflowStatus();

  React.useEffect(() => {
    if (topLevelData) {
      setCurrentData(topLevelData);
    }
  }, [topLevelData]);


  function handleDeleteClick(e: React.MouseEvent, item: WorkflowAction) {
    e.stopPropagation();
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  }

  function handleConfirmDelete() {
    if (!itemToDelete) return;
    deletePortalForm.mutate(itemToDelete.PMId);
  }

  React.useEffect(() => {
    if (deletePortalForm.isSuccess) {
      setDeleteDialogOpen(false);
      setReqStatus(true);
      setAlertMessage("Workflow deleted successfully!");
      setItemToDelete(null);
      setAlertOpen(true);
      deletePortalForm.reset();
    } else if (deletePortalForm.isError) {
      const raw = deletePortalForm.error as Error;
      setReqStatus(false);
      setDeleteDialogOpen(false);
      setAlertMessage(parseApiErrorMessage(raw.message));
      setAlertOpen(true);
      deletePortalForm.reset();
    }
  }, [deletePortalForm.isSuccess, deletePortalForm.isError]);


  function handleCheckInOutClick(e: React.MouseEvent, item: WorkflowAction) {
    e.stopPropagation();
    setItemToCheckInOut(item);
    setCheckInOutDialogOpen(true);
  }

  function handleConfirmCheckInOut(comment: string) {
    if (!itemToCheckInOut) return;
    togglePublishMutation.mutate({
      Id: itemToCheckInOut.PMId,
      data: {
        UserName: itemToCheckInOut.LastUser,
        Comment: comment,
      },
    });
  }

  function handleActivateClick(e: React.MouseEvent, item: WorkflowAction) {
    e.stopPropagation();
    setItemToToggle(item);
    setToggleMode("activate");
    setApproveDialogOpen(true);
  }

  function handleConfirmToggle() {
    if (!itemToToggle) return;
    statusActiveMutation.mutate(itemToToggle.PMId);
  }

  React.useEffect(() => {
    if (togglePublishMutation.isSuccess) {
      setCheckInOutDialogOpen(false);
      setReqStatus(true);
      const msg =
        itemToCheckInOut?.Status === 2 || itemToCheckInOut?.Status === 0
          ? "Workflow Checked In successfully!"
          : "Workflow Checked Out successfully!";
      setAlertMessage(msg);
      setItemToCheckInOut(null);
      setAlertOpen(true);
      togglePublishMutation.reset();
    } else if (togglePublishMutation.isError) {
      const raw = togglePublishMutation.error as Error;
      setReqStatus(false);
      setCheckInOutDialogOpen(false);
      setAlertMessage(parseApiErrorMessage(raw.message));
      setAlertOpen(true);
      togglePublishMutation.reset();
    }
  }, [togglePublishMutation.isSuccess, togglePublishMutation.isError]);

  React.useEffect(() => {
    if (statusActiveMutation.isSuccess) {
      setApproveDialogOpen(false);
      setReqStatus(true);
      setAlertMessage(
        itemToToggle?.isActive
          ? "Workflow Deactivated successfully!"
          : "Workflow Activated successfully!"
      );
      setItemToToggle(null);
      setAlertOpen(true);
      statusActiveMutation.reset();
    } else if (statusActiveMutation.isError) {
      const raw = statusActiveMutation.error as Error;
      setReqStatus(false);
      setApproveDialogOpen(false);
      setAlertMessage(parseApiErrorMessage(raw.message));
      setAlertOpen(true);
      statusActiveMutation.reset();
    }
  }, [statusActiveMutation.status]);


  function handleViewDescription(item: WorkflowAction) {
    setPopupName("Description");
    setHeadingData(item.WorkflowName)
    setDescriptionContent(item.Description);
    setShowPopup(true);
  }


  const confirmHeading =
    toggleMode === "checkinout"
      ? itemToToggle?.Status === 2
        ? "Confirm Check In"
        : "Confirm Check Out"
      : itemToToggle?.isActive
        ? "Confirm Deactivate"
        : "Confirm Activate";

  const confirmMessage =
    toggleMode === "checkinout"
      ? `Are you sure you want to ${itemToToggle?.Status === 2 ? "Check In" : "Check Out"} this workflow?`
      : `Are you sure you want to ${itemToToggle?.isActive ? "Deactivate" : "Activate"} this workflow?`;

  const confirmLabel =
    toggleMode === "checkinout"
      ? itemToToggle?.Status === 2
        ? "Check In"
        : "Check Out"
      : itemToToggle?.isActive
        ? "Deactivate"
        : "Activate";


  return (
    <div>
      <div className="border bg-white border-gray-200 rounded-lg p-2">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>PM Id</TableHead>
              <TableHead>WF Id</TableHead>
              <TableHead>Workflow Name</TableHead>
              <TableHead>Process Type</TableHead>
              <TableHead>WF Type</TableHead>
              <TableHead>Build</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Ver#</TableHead>
              <TableHead>Active</TableHead>
              <TableHead>Last User</TableHead>
              <TableHead>Created Time</TableHead>
              <TableHead>Last Modified</TableHead>
              <TableHead>Actions</TableHead>
              <TableHead>Version</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {currentData?.map((data) => (
              <TableRow key={data.PMId}>
                <TableCell className="font-mono">{data.PMId}</TableCell>
                <TableCell className="font-mono">{data.WFId}</TableCell>
                <TableCell className="font-mono">{data.WorkflowName}</TableCell>
                <TableCell className="font-mono">{data.ProcessType}</TableCell>
                <TableCell className="font-mono">{data.WorkFlowSubCategory_Name}</TableCell>

                <TableCell className="max-w-xs truncate">
                  <Button
                    variant="link"
                    onClick={() => {
                      if (data?.PMId) {
                        window.open(
                          `https://form-engine.workfinity.com/designer/${data.PMId}`,
                          "_blank"
                        );
                      }
                    }}
                  >
                    <RemoveFormatting />
                  </Button>
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  <Button
                    variant="link"
                    onClick={() => handleViewDescription(data)}
                  >
                    View
                  </Button>
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  {data.Status === 0
                    ? "New Workflow"
                    : data.Status === 1
                      ? "Checked In"
                      : "Checked Out"}
                </TableCell>

                <TableCell className="max-w-xs truncate">{data.VersionNumber}</TableCell>

                <TableCell className="max-w-xs truncate">
                  {data.isActive ? (
                    <Check className="h-4 w-4 mr-1 text-green-800" />
                  ) : (
                    <X className="h-4 w-4 mr-1 text-red-800" />
                  )}
                </TableCell>

                <TableCell className="max-w-xs truncate">{data.LastUser}</TableCell>

                <TableCell className="max-w-xs">
                  <div className="flex flex-col">
                    {/* <span>
                      {new Date(data.DateCreated).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(data.DateCreated).toLocaleTimeString("en-IN", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span> */}
                    {data.DateCreated ? (
                      <>
                        <span>
                          {new Date(data.DateCreated).toLocaleDateString("en-IN", {
                            day: "2-digit", month: "short", year: "numeric",
                          })}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(data.DateCreated).toLocaleTimeString("en-IN", {
                            hour: "2-digit", minute: "2-digit",
                          })}
                        </span>
                      </>
                    ) : "-"}
                  </div>
                </TableCell>

                <TableCell className="max-w-xs">
                  <div className="flex flex-col">
                    {/* <span>
                      {new Date(data.LastModified).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(data.LastModified).toLocaleTimeString("en-IN", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span> */}
                    {data.LastModified ? (
                      <>
                        <span>
                          {new Date(data.LastModified).toLocaleDateString("en-IN", {
                            day: "2-digit", month: "short", year: "numeric",
                          })}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(data.LastModified).toLocaleTimeString("en-IN", {
                            hour: "2-digit", minute: "2-digit",
                          })}
                        </span>
                      </>
                    ) : "-"}
                  </div>
                </TableCell>

                <TableCell>
                  <div className="flex flex-row items-center space-x-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="min-w-6 h-6 p-0"
                          tooltip="More actions"
                        >
                          <Plus className="h-3 w-3" />
                          <span className="sr-only">More actions</span>
                        </Button>
                      </DropdownMenuTrigger>

                      <DropdownMenuContent align="end" className="min-w-[180px]">
                        <DropdownMenuItem
                          onClick={(e) => handleCheckInOutClick(e, data)}
                          className="flex items-center px-1 cursor-pointer"
                        >
                          {data.Status === 1 ? (
                            <X className="h-4 w-4 mr-1 text-red-600" />
                          ) : (
                            <Check className="h-4 w-4 mr-1 text-green-600" />
                          )}
                          <span className="text-sm">
                            {data.Status === 1 ? "Check Out" : "Check In"}
                          </span>
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          className="flex items-center px-1 cursor-pointer"
                          onClick={() => onEditForm(data)}
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          <span className="text-sm">Edit Workflow</span>
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          onClick={() => {
                            if (data?.WFId) {
                              window.open(
                                `https://form-engine.workfinity.com/preview/${data.WFId}`,
                                "_blank"
                              );
                            }
                          }}
                          className="flex items-center px-1 cursor-pointer"
                        >
                          <View className="h-4 w-4 mr-1" />
                          <span className="text-sm">View Workflow</span>
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          onClick={(e) => handleActivateClick(e, data)}
                          className="flex items-center px-1 cursor-pointer"
                        >
                          {data.isActive ? (
                            <X className="h-4 w-4 mr-1 text-red-600" />
                          ) : (
                            <Check className="h-4 w-4 mr-1 text-green-600" />
                          )}
                          <span className="text-sm">
                            {data.isActive ? "Deactivate" : "Activate"}
                          </span>
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          onClick={(e) => handleDeleteClick(e, data)}
                          className="flex items-center px-1 cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          <span className="text-sm">Delete</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  <Button
                    variant="link"
                    onClick={() => {
                      setLocation(`/workflowversion/${data.PMId}`);
                    }}
                  >
                    Version History
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="mt-2">
          <PaginationControl
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={onPageChange}
          />
        </div>
      </div>

      <AlertDialog
        open={alertOpen}
        onOpenChange={setAlertOpen}
        message={alertMessage}
        status={reqStatus}
      />

      {showPopup && (
        <Popup
          popupName={popupName}
          heading={headingData}
          content={descriptionContent}
          onClose={() => setShowPopup(false)}
        />
      )}

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        isPending={deletePortalForm.isPending}
        itemName={itemToDelete as any}
        itemType="Workflow"
        requireAuthCode={false}
      />

      <CheckInOutDialog
        open={checkInOutDialogOpen}
        onOpenChange={setCheckInOutDialogOpen}
        onConfirm={handleConfirmCheckInOut}
        isPending={togglePublishMutation.isPending}
        apiStatus={togglePublishMutation}
        workflowName={itemToCheckInOut?.WorkflowName}
        versionNumber={itemToCheckInOut?.VersionNumber}
        username={itemToCheckInOut?.LastUser}
        mode={itemToCheckInOut?.Status === 2 ? "checkin" : "checkout"}
      />

      <ConfirmationDialog
        open={approveDialogOpen}
        onOpenChange={setApproveDialogOpen}
        onConfirm={handleConfirmToggle}
        isPending={statusActiveMutation.isPending}
        heading={confirmHeading}
        message={confirmMessage}
        confirmLabel={confirmLabel}
      />
    </div>
  );
}


export function WorkflowEngine() {
  const { setBreadcrumbs } = useBreadcrumbs();

  const [topLevelTypes, setTopLevelTypes] = useState<WorkflowAction[]>([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [filterOpen, setFilterOpen] = useState(false);

  const [wfIdInput, setWfIdInput] = useState<string>("");
  const [wfNameInput, setWfNameInput] = useState<string>("");
  const [wfStatusInput, setWfStatusInput] = useState<string>("");

  const [appliedWfId, setAppliedWfId] = useState<string | undefined>();
  const [appliedWfName, setAppliedWfName] = useState<string | undefined>();
  const [appliedWfStatus, setAppliedWfStatus] = useState<string | undefined>();

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [editFormData, setEditFormData] = useState<any>(null);

  const { data: types, isLoading } = useWorkflowRepository(
    pageIndex,
    pageSize,
    appliedWfId,
    appliedWfName,
    appliedWfStatus
  );

  React.useEffect(() => {
    setBreadcrumbs([{ label: "Workflow", path: `/` }]);
  }, []);

  React.useEffect(() => {
    setTopLevelTypes(types?.items ?? []);
    setTotalCount(types?.total ?? 0);
  }, [types]);

  const handleSearch = () => {
    setAppliedWfId(wfIdInput.trim() || undefined);
    setAppliedWfName(wfNameInput.trim() || undefined);
    setAppliedWfStatus(wfStatusInput || undefined);
    setPageIndex(1);
  };

  const handleRefresh = (close: boolean) => {
    setWfIdInput("");
    setWfNameInput("");
    setWfStatusInput("");
    setAppliedWfId(undefined);
    setAppliedWfName(undefined);
    setAppliedWfStatus(undefined);
    setPageIndex(1);
    if (close) setFilterOpen(false);
  };

  const handleAddForm = () => {
    setEditFormData(null);
    setOpenCreateDialog(true);
  };

  const handleEditForm = (data: any) => {
    setEditFormData(data);
    setOpenCreateDialog(true);
  };


  return (
    <div className="bg-gray-50 min-h-full">

      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div className="min-w-0">
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">
              Manage WorkFlow
            </h2>
            <p className="text-gray-600 mt-1 text-sm md:text-base">
              Use workflow designer to create and manage application workflow including sub-processes
            </p>
          </div>

          <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
            <Button
              className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
              size="sm"
              onClick={() => setFilterOpen((prev) => !prev)}
            >
              Filter Workflow
            </Button>
            <Button
              className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
              size="sm"
              onClick={handleAddForm}
            >
              <Plus className="h-3 w-3 md:h-4 md:w-4 mr-2" />
              Add Workflow
            </Button>
          </div>
        </div>
      </div>

      {filterOpen && (
        <Card className="mb-4 md:mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row md:items-end md:space-x-4 space-y-3 md:space-y-0">
              <div className="flex-1">
                <label className="text-sm font-medium mb-1.5 block">PM Id</label>
                <Input
                  placeholder="Enter PM Id"
                  value={wfIdInput}
                  onChange={(e) => setWfIdInput(e.target.value)}
                />
              </div>

              <div className="flex-1">
                <label className="text-sm font-medium mb-1.5 block">Workflow Name</label>
                <Input
                  placeholder="Enter Workflow Name"
                  value={wfNameInput}
                  onChange={(e) => setWfNameInput(e.target.value)}
                />
              </div>

              <div className="flex-1">
                <label className="text-sm font-medium mb-1.5 block">Status</label>
                <Select
                  value={wfStatusInput}
                  onValueChange={(value) => setWfStatusInput(value === "all" ? "" : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="0">New Workflow</SelectItem>
                    <SelectItem value="1">Checked In</SelectItem>
                    <SelectItem value="2">Checked Out</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-4 flex justify-center space-x-2">
              <Button onClick={handleSearch}>Search</Button>
              <Button variant="outline" onClick={() => handleRefresh(false)}>
                Refresh
              </Button>
              <Button variant="outline" onClick={() => handleRefresh(true)}>
                Close
              </Button>
            </div>
          </CardContent>
        </Card>
      )}


      {!isLoading ? (
        <Card>
          <CardContent className="p-4 md:p-6">
            <div className="space-y-2">
              {topLevelTypes && topLevelTypes.length > 0 ? (
                <TableForm
                  topLevelData={topLevelTypes}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  totalCount={totalCount}
                  onPageChange={setPageIndex}
                  onEditForm={handleEditForm}
                />
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No Workflow found. Create one to get started.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="p-6">Loading...</div>
      )}

      {
        openCreateDialog &&
        <CreateWorkflow
          open={openCreateDialog}
          onOpenChange={setOpenCreateDialog}
          initialData={editFormData}
          offerData={null}
        />
      }

    </div>
  );
}